// proposalItemsModel.js
//
// Field reads, pricing math, and rollups for Proposal Items.

import { parsePriceCents } from 'utilities/Currency';
import { buildNodeFieldPatch, readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getEnumOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { getNodelistPrimaryKey } from 'utilities/NodelistField';
import {
  PROPOSAL_ITEM_TYPE_KEY,
  readProposalField,
} from 'nodetypes/proposal/view/proposalGeneralInfoModel';
import {
  computePurchaseOrderItemLineTotal,
  toMoneyNumber,
  toQuantityNumber,
} from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';
import { sumPaidDocumentPayments } from 'nodetypes/documents/payments/documentPaymentsModel';

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeTypeKey(value) {
  return normalizeString(value).toUpperCase();
}

/**
 * @param {object|null|undefined} item
 * @param {string} fieldName
 * @returns {unknown}
 */
export function readProposalItemField(item, fieldName) {
  return readNodeFieldRawValue(
    item,
    { name: fieldName, source: 'proposal_item' },
    PROPOSAL_ITEM_TYPE_KEY,
  );
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function toPercentNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Markup amount in cents: unitPrice − cost.
 *
 * @param {unknown} cost
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computeMarkupAmountCents(cost, unitPrice) {
  const costCents = toMoneyNumber(cost);
  const priceCents = toMoneyNumber(unitPrice);
  if (costCents == null || priceCents == null) return null;
  return priceCents - costCents;
}

/**
 * Unit price from cost + markup percent.
 *
 * @param {unknown} cost
 * @param {unknown} markupPercent
 * @returns {number|null}
 */
export function computeUnitPriceFromCostAndMarkup(cost, markupPercent) {
  const costCents = toMoneyNumber(cost);
  const markup = toPercentNumber(markupPercent);
  if (costCents == null) return null;
  if (markup == null) return costCents;
  return Math.round(costCents * (1 + (markup / 100)));
}

/**
 * Markup % from cost + unit price.
 *
 * @param {unknown} cost
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computeMarkupPercentFromCostAndPrice(cost, unitPrice) {
  const costCents = toMoneyNumber(cost);
  const priceCents = toMoneyNumber(unitPrice);
  if (costCents == null || priceCents == null || costCents === 0) return null;
  return Math.round(((priceCents - costCents) / costCents) * 10000) / 100;
}

/**
 * Margin % from cost + unit price: (price − cost) / price.
 *
 * @param {unknown} cost
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computeMarginPercent(cost, unitPrice) {
  const costCents = toMoneyNumber(cost);
  const priceCents = toMoneyNumber(unitPrice);
  if (costCents == null || priceCents == null || priceCents === 0) return null;
  return Math.round(((priceCents - costCents) / priceCents) * 10000) / 100;
}

/**
 * Line total: qty × unitPrice (sell).
 *
 * @param {unknown} quantity
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computeProposalItemLineTotal(quantity, unitPrice) {
  return computePurchaseOrderItemLineTotal(quantity, unitPrice);
}

/**
 * @param {object|null|undefined} item
 * @returns {number|null}
 */
export function resolveProposalItemLineTotal(item) {
  const quantity = toQuantityNumber(readProposalItemField(item, 'quantity'));
  const unitPrice = toMoneyNumber(readProposalItemField(item, 'unitPrice'));
  const derived = computeProposalItemLineTotal(quantity, unitPrice);
  if (derived != null) return derived;
  return toMoneyNumber(readProposalItemField(item, 'total'));
}

/**
 * Merge computed pricing fields into a field patch when cost / markup / unitPrice / qty change.
 *
 * @param {object} options
 * @returns {object|null|undefined}
 */
export function augmentProposalItemPricingPatch({
  node,
  nodeTypeKey,
  field,
  value,
  patch,
}) {
  if (!patch || !node) return patch;
  if (normalizeTypeKey(nodeTypeKey || node?.type) !== PROPOSAL_ITEM_TYPE_KEY) {
    return patch;
  }

  const fieldName = normalizeString(field?.name);
  const pricingFields = new Set(['quantity', 'cost', 'markupPercent', 'unitPrice']);
  if (!pricingFields.has(fieldName)) return patch;

  let cost = fieldName === 'cost' ? value : readProposalItemField(node, 'cost');
  let markupPercent = fieldName === 'markupPercent'
    ? value
    : readProposalItemField(node, 'markupPercent');
  let unitPrice = fieldName === 'unitPrice'
    ? value
    : readProposalItemField(node, 'unitPrice');
  const quantity = fieldName === 'quantity'
    ? value
    : readProposalItemField(node, 'quantity');

  if (fieldName === 'cost') {
    const nextPrice = computeUnitPriceFromCostAndMarkup(cost, markupPercent);
    if (nextPrice != null) unitPrice = nextPrice;
    else if (toMoneyNumber(unitPrice) != null) {
      markupPercent = computeMarkupPercentFromCostAndPrice(cost, unitPrice);
    }
  } else if (fieldName === 'markupPercent') {
    const nextPrice = computeUnitPriceFromCostAndMarkup(cost, markupPercent);
    if (nextPrice != null) unitPrice = nextPrice;
  } else if (fieldName === 'unitPrice') {
    if (toMoneyNumber(cost) != null) {
      markupPercent = computeMarkupPercentFromCostAndPrice(cost, unitPrice);
    }
  }

  const marginPercent = computeMarginPercent(cost, unitPrice);
  const total = computeProposalItemLineTotal(quantity, unitPrice);

  const source = field?.source || 'proposal_item';
  const merged = { ...patch };

  const applyExtra = (name, type, nextValue) => {
    if (nextValue === undefined) return;
    const extraField = { name, source, type };
    const extraPatch = buildNodeFieldPatch(node, extraField, nodeTypeKey, nextValue);
    if (!extraPatch) return;
    Object.entries(extraPatch).forEach(([dataKey]) => {
      const existingSection = (
        merged[dataKey] && typeof merged[dataKey] === 'object'
          ? merged[dataKey]
          : {}
      );
      merged[dataKey] = {
        ...existingSection,
        [name]: nextValue,
      };
    });
  };

  if (fieldName !== 'cost' && toMoneyNumber(cost) != null) {
    // cost unchanged — no write
  }
  if (fieldName !== 'unitPrice' && unitPrice !== readProposalItemField(node, 'unitPrice')) {
    applyExtra('unitPrice', 'money', unitPrice);
  }
  if (fieldName !== 'markupPercent' && markupPercent !== readProposalItemField(node, 'markupPercent')) {
    applyExtra('markupPercent', 'percentage', markupPercent);
  }
  if (marginPercent != null) {
    applyExtra('marginPercent', 'percentage', marginPercent);
  }
  if (total != null) {
    applyExtra('total', 'money', total);
  }

  return merged;
}

/**
 * @param {object|null|undefined} item
 * @param {object|null|undefined} [itemType]
 * @param {string} [language='en']
 * @returns {object}
 */
export function getProposalItemRowModel(item, itemType = null, language = 'en') {
  const fields = Array.isArray(itemType?.schema?.fields) ? itemType.schema.fields : [];
  const enumLabel = (fieldName, value) => {
    const cleaned = normalizeString(value);
    if (!cleaned) return '';
    const field = fields.find((row) => normalizeString(row?.name) === fieldName);
    if (!field) return cleaned;
    const options = resolveEnumOptionsForField(field, language);
    return getEnumOptionLabel(options, cleaned, language) || cleaned;
  };

  const cost = toMoneyNumber(readProposalItemField(item, 'cost'));
  const unitPrice = toMoneyNumber(readProposalItemField(item, 'unitPrice'));
  const markupPercent = toPercentNumber(readProposalItemField(item, 'markupPercent'));
  const storedMargin = toPercentNumber(readProposalItemField(item, 'marginPercent'));
  const marginPercent = storedMargin != null
    ? storedMargin
    : computeMarginPercent(cost, unitPrice);
  const markupAmount = computeMarkupAmountCents(cost, unitPrice);

  const unitValue = normalizeString(readProposalItemField(item, 'unit'));
  const description = normalizeString(getNodeSubtitle(item));
  const leadTime = normalizeString(readProposalItemField(item, 'leadTime'));

  return {
    key: normalizeString(item?.key),
    title: normalizeString(item?.title),
    description,
    subtitle: description || leadTime,
    leadTime,
    coverUrl: normalizeString(item?.coverUrl),
    sku: normalizeString(readProposalItemField(item, 'sku')),
    manufacturer: normalizeString(readProposalItemField(item, 'manufacturer')),
    vendorKey: getNodelistPrimaryKey(readProposalItemField(item, 'vendor')) || '',
    roomKey: getNodelistPrimaryKey(readProposalItemField(item, 'room')) || '',
    itemType: normalizeString(readProposalItemField(item, 'itemType')),
    itemTypeLabel: enumLabel('itemType', readProposalItemField(item, 'itemType')),
    category: normalizeString(readProposalItemField(item, 'category')),
    categoryLabel: enumLabel('category', readProposalItemField(item, 'category')),
    status: normalizeString(readProposalItemField(item, 'status')),
    quantity: toQuantityNumber(readProposalItemField(item, 'quantity')),
    unit: unitValue,
    unitLabel: enumLabel('unit', unitValue) || unitValue,
    cost,
    msrp: toMoneyNumber(readProposalItemField(item, 'msrp')),
    markupPercent,
    markupAmount,
    marginPercent,
    unitPrice,
    color: normalizeString(readProposalItemField(item, 'color')),
    dimensions: normalizeString(readProposalItemField(item, 'dimensions')),
    shipping: toMoneyNumber(readProposalItemField(item, 'shipping')),
    shippingMarkupPercent: toPercentNumber(readProposalItemField(item, 'shippingMarkupPercent')),
    taxable: readProposalItemField(item, 'taxable') !== false,
    website: normalizeString(readProposalItemField(item, 'website')),
    total: resolveProposalItemLineTotal(item),
  };
}

/**
 * @param {object[]} items
 * @param {object|null|undefined} proposalNode
 * @param {object|null|undefined} [proposalType]
 * @param {object[]|null} [payments]
 * @returns {{ subTotal: number, tax: number, total: number, amountPaid: number, balanceDue: number }}
 */
export function computeProposalItemsRollups(
  items,
  proposalNode = null,
  proposalType = null,
  payments = null,
) {
  const subTotal = (Array.isArray(items) ? items : []).reduce((sum, item) => {
    const lineTotal = resolveProposalItemLineTotal(item);
    return sum + (lineTotal == null ? 0 : lineTotal);
  }, 0);

  const tax = toMoneyNumber(readProposalField(proposalNode, 'tax', proposalType)) || 0;
  const total = subTotal + tax;

  let amountPaid;
  if (Array.isArray(payments) && payments.length > 0) {
    amountPaid = sumPaidDocumentPayments(payments);
  } else {
    amountPaid = toMoneyNumber(readProposalField(proposalNode, 'amountPaid', proposalType)) || 0;
  }

  return {
    subTotal,
    tax,
    total,
    amountPaid,
    balanceDue: total - amountPaid,
  };
}
